import { IntegrationTraceEmitter, TraceEventInput, recordEmit } from "./emitter";
import { TracePointId } from "../flatbuffers/generated/integration-trace/trace-point-id";
import { TraceOutcome } from "../flatbuffers/generated/integration-trace/trace-outcome";
import { TracePath } from "../flatbuffers/generated/integration-trace/trace-path";

export { IntegrationTraceEmitter } from "./emitter";
export { uuidStringToU128T, buildBatchBytes, getEmitCount, resetEmitCounters } from "./emitter";
export type { TraceEventInput } from "./emitter";
export { TracePointId } from "../flatbuffers/generated/integration-trace/trace-point-id";
export { SourceComponent } from "../flatbuffers/generated/integration-trace/source-component";
export { TraceOutcome } from "../flatbuffers/generated/integration-trace/trace-outcome";
export { TracePath } from "../flatbuffers/generated/integration-trace/trace-path";

/**
 * No-op emitter returned when REACT_APP_INTEGRATION_TRACE_WS is not configured.
 * Has the same interface as IntegrationTraceEmitter so call sites need no env guards.
 */
class NoOpIntegrationTraceEmitter {
  // eslint-disable-next-line class-methods-use-this
  emit(_input: TraceEventInput): void {
    // intentional no-op
  }

  // eslint-disable-next-line class-methods-use-this
  flush(): void {
    // intentional no-op
  }

  // eslint-disable-next-line class-methods-use-this
  disconnect(): void {
    // intentional no-op
  }
}

type AnyTraceEmitter = IntegrationTraceEmitter | NoOpIntegrationTraceEmitter;

let emitterInstance: AnyTraceEmitter | null = null;

/**
 * Returns the process-singleton emitter (or a no-op when env var is absent).
 * Lazily instantiated on first call.
 */
export function getIntegrationTraceEmitter(): AnyTraceEmitter {
  if (emitterInstance !== null) return emitterInstance;

  const wsUrl = process.env.REACT_APP_INTEGRATION_TRACE_WS;
  if (!wsUrl || wsUrl.trim() === "") {
    emitterInstance = new NoOpIntegrationTraceEmitter();
  } else {
    emitterInstance = new IntegrationTraceEmitter(wsUrl.trim());
  }
  return emitterInstance;
}

/**
 * Convenience helper: emit a single trace event via the singleton emitter.
 *
 * @param tracePoint - The FBS TracePointId enum value
 * @param correlationId - UUID v4 string; generated automatically when omitted
 * @param opts - Optional outcome, path, and errorMessage overrides
 */
export function tracePointEmit(
  tracePoint: TracePointId,
  correlationId?: string,
  opts?: {
    outcome?: TraceOutcome;
    path?: TracePath;
    errorMessage?: string;
  },
): void {
  getIntegrationTraceEmitter().emit({
    tracePoint,
    correlationId,
    outcome: opts?.outcome,
    path: opts?.path,
    errorMessage: opts?.errorMessage,
  });

  const outcome =
    opts?.outcome === TraceOutcome.Error ||
    opts?.outcome === TraceOutcome.Timeout ||
    opts?.outcome === TraceOutcome.Dropped
      ? "err"
      : "ok";
  recordEmit("fbs", tracePoint, outcome);
}

/**
 * Resets the singleton for testing purposes only. Allows unit tests to inject a fresh emitter.
 * Should never be called in production code.
 */
export function resetEmitterSingleton(): void {
  if (emitterInstance instanceof IntegrationTraceEmitter) {
    emitterInstance.disconnect();
  }
  emitterInstance = null;
}
